import { Badge } from '@/components/ui/badge';
import type { ComplianceEngineMetadata, ComplianceLineItem } from '@/services';
import { formatComplianceCurrency } from './ComplianceLineItemDetail';

interface AIComplianceSummaryBarProps {
  metadata: ComplianceEngineMetadata | null | undefined;
  lineItems: ComplianceLineItem[];
}

export function AIComplianceSummaryBar({ metadata, lineItems }: AIComplianceSummaryBarProps) {
  const total = lineItems.length;
  const passed = lineItems.filter(i => i.status === 'PASS').length;
  const failed = lineItems.filter(i => i.status === 'FAIL').length;
  const flagged = lineItems.filter(i => i.status === 'FLAG').length;
  const calculatedOvercharge = lineItems.reduce((sum, item) => {
    if (item.types === 'overcharge' && item.total_impact != null) {
      return sum + item.total_impact;
    }
    return sum;
  }, 0);
  const calculatedUndercharge = lineItems.reduce((sum, item) => {
    if (item.types === 'undercharge' && item.total_impact != null) {
      return sum + item.total_impact;
    }
    return sum;
  }, 0);
  const PillBadge = ({ label, included, message }: { label: string; included: boolean; message?: string | null }) => (
    <div
      className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
        included
          ? 'bg-success/10 text-success border border-success/30'
          : 'bg-muted/50 text-muted-foreground border border-border'
      }`}
      title={message || undefined}
    >
      <span>{included ? '✓' : '✗'}</span>
      {label}
    </div>
  );

  return (
    <div className="flex items-stretch bg-card border border-border rounded-lg overflow-hidden">
      {/* Left: Data included */}
      {metadata && (
        <div className="flex-1 p-4 border-r border-border">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Data Analyzed:</div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground w-20 shrink-0">Price Book</span>
              <PillBadge label="Items" included={metadata.price_book_items_count > 0} />
              <PillBadge label="PDF" included={metadata.price_book_pdf} message={metadata.price_book_pdf_message} />
              <PillBadge label="Brief" included={metadata.price_book_brief} message={metadata.price_book_brief_message} />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground w-20 shrink-0">Ticket</span>
              <PillBadge label="Items" included={metadata.ticket_items_count > 0} />
              <PillBadge label="PDF" included={metadata.field_ticket_pdf} message={metadata.field_ticket_pdf_message} />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground w-20 shrink-0">Invoice</span>
              <PillBadge label="Items" included={metadata.invoice_items_count > 0} />
            </div>
          </div>
        </div>
      )}

      {/* Middle: Items processed */}
      <div className="p-4 border-r border-border flex flex-col justify-center">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Item Analysis Results:</div>
        <div className="flex gap-2">
          <Badge variant="outline" className="flex flex-col items-center justify-center rounded-lg px-4 py-2 text-xs border-yellow-600 text-yellow-500">
            <span>Flagged</span>
            <span className="text-sm font-semibold">{flagged}</span>
          </Badge>
          <Badge variant="outline" className="flex flex-col items-center justify-center rounded-lg px-4 py-2 text-xs border-green-600 text-green-500">
            <span>Passed</span>
            <span className="text-sm font-semibold">{passed}</span>
          </Badge>
          <Badge variant="outline" className="flex flex-col items-center justify-center rounded-lg px-4 py-2 text-xs border-red-600 text-red-500">
            <span>Failed</span>
            <span className="text-sm font-semibold">{failed}</span>
          </Badge>
        </div>
      </div>

      {/* Right: Overcharge / Undercharge */}
      <div className="p-4 flex gap-3 items-center">
        <div className="border-2 border-red-500 rounded-lg px-5 py-3 text-center min-w-[140px]">
          <div className="flex items-center justify-center">
            <span className="text-2xl font-bold text-red-500">${calculatedOvercharge.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-1">Overcharge</div>
        </div>
        <div className="border-2 border-blue-500 rounded-lg px-5 py-3 text-center min-w-[140px]">
          <div className="flex items-center justify-center">
            <span className="text-2xl font-bold text-blue-500">${calculatedUndercharge.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-1">Undercharge</div>
        </div>
      </div>
    </div>
  );
}
